import { useLocation } from "@remix-run/react";
import { useCallback, useEffect, useRef } from "react";
import { safeSessionStorage } from "../utils/session-storage";

type RouteEntry = {
  path: string;
  timestamp: number;
  referer?: string;
};

type UseRouteTrackerReturn = {
  getNavigationHistory: () => RouteEntry[];
  clearNavigationHistory: () => void;
};

const STORAGE_KEY = "navigation_history";
const MAX_HISTORY_SIZE = 20; // Limit to prevent excessive storage usage

export function useRouteTracker(): UseRouteTrackerReturn {
  const location = useLocation();
  const isInitialized = useRef(false);

  // Get navigation history from session storage
  const getNavigationHistory = useCallback((): RouteEntry[] => {
    if (typeof window === "undefined") return [];

    try {
      const stored = safeSessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (error) {
      console.warn("Failed to parse navigation history:", error);
      return [];
    }
  }, []);

  // Save navigation history to session storage
  const saveNavigationHistory = useCallback((history: RouteEntry[]) => {
    if (typeof window === "undefined") return;

    try {
      safeSessionStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (error) {
      console.warn("Failed to save navigation history:", error);
    }
  }, []);

  // Clear navigation history
  const clearNavigationHistory = useCallback(() => {
    if (typeof window === "undefined") return;

    try {
      safeSessionStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn("Failed to clear navigation history:", error);
    }
  }, []);

  // Track route changes
  useEffect(() => {
    if (typeof window === "undefined") return;

    const currentPath = `${location.pathname}${location.search}`;
    const currentTimestamp = Date.now();

    // Skip the initial load to avoid duplicate entries
    if (!isInitialized.current) {
      isInitialized.current = true;

      // Add initial route
      const initialEntry: RouteEntry = {
        path: currentPath,
        timestamp: currentTimestamp,
        referer: document.referrer || undefined,
      };

      saveNavigationHistory([initialEntry]);
      return;
    }

    // Get current history
    const history = getNavigationHistory();

    // Create new entry
    const newEntry: RouteEntry = {
      path: currentPath,
      timestamp: currentTimestamp,
      referer:
        history.length > 0 ? history[history.length - 1].path : undefined,
    };

    // Add new entry and limit history size
    const updatedHistory = [...history, newEntry].slice(-MAX_HISTORY_SIZE);

    saveNavigationHistory(updatedHistory);
  }, [
    location.pathname,
    location.search,
    getNavigationHistory,
    saveNavigationHistory,
  ]);

  return {
    getNavigationHistory,
    clearNavigationHistory,
  };
}
